import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CourseData, CoursemakerUploadRequest, CoursemakerUploadResponse } from "@vixi/types";
import { AxiosProgressEvent } from "axios";
import { isMock } from "@/mocks";
import api, { formdataFromObj } from "@vixi/api";
import { sleepAsync, courseGenerationToCourse, forEachLesson, logger } from "@vixi/utils";

const setDefaultSelectedTitle = (lesson: CourseData.Lesson) => {
  if (lesson.selectedTitle === undefined) {
    lesson.selectedTitle = 0;
  }
};

const mock = [
  {
    titles: [
      "Your Guide to Software Professionalism",
      "How to Master Software Development",
      "What if Coding Was Professional?",
      "Why Professional Programmers Lead the Way",
      "Becoming a Software Craftsman",
      "The Clean Path to Programming",
    ],
    conversations: [
      {
        conversation_id: 1,
        messages: [
          {
            author_message:
              "Hey there! Have you ever wondered what makes a 'professional programmer' stand out?",
            learner_message: "Yeah, I’ve heard the phrase but what does it really mean?",
          },
          {
            author_message:
              "It’s not just about writing code. It involves taking responsibility, collaborating effectively, managing your time, and using the right tools to contribute to the growth of software development as a profession.",
            learner_message: "Taking responsibility sounds serious, can you give me an example?",
          },
          {
            author_message:
              "Sure! For example, imagine committing to fix a bug by a certain deadline. A professional ensures they deliver on time and communicates progress clearly along the way.",
            learner_message: "That makes sense. Why is collaboration part of being professional?",
          },
          {
            author_message:
              "Software development often involves teams. Professionals actively share ideas, resolve conflicts, and ensure everyone aligns with project goals. It’s like building a house—you need the plumber, electrician, and architect working together!",
            learner_message: "Interesting analogy! How do tools play into this?",
          },
          {
            author_message:
              "Tools help you work efficiently and maintain quality. For instance, a professional programmer uses testing tools like TDD (Test-Driven Development) and ATDD (Acceptance Test-Driven Development) to ensure their code works well.",
            learner_message: "I’ve heard of TDD but ATDD is new to me. What’s the difference?",
          },
          {
            author_message:
              "TDD ensures functionality by testing small pieces of code as you go. ATDD involves broader testing, focusing on whether the entire application meets user expectations.",
            learner_message:
              "Oh wow, I didn’t know they focus on different aspects. Is there more about professionalism we should explore?",
          },
          {
            author_message:
              "Absolutely! Next, we'll dive into the journey of becoming a software professional—it’s not just following rules; it's mastering craftsmanship. Excited?",
            learner_message:
              "This sounds fascinating, let’s explore the journey to becoming a software professional next!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What does professionalism in programming involve?",
            answers: [
              "Just writing code",
              "Responsible actions and collaboration",
              "Using outdated tools",
            ],
            correct_answer: "Responsible actions and collaboration",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What is the main focus of ATDD?",
            answers: ["Small code testing", "User expectations", "Debugging"],
            correct_answer: "User expectations",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the following practices with their descriptions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "TDD",
                answer: "Testing small pieces of code",
              },
              {
                question: "ATDD",
                answer: "Ensuring application meets user needs",
              },
              {
                question: "Collaboration",
                answer: "Sharing ideas and resolving conflicts",
              },
            ],
          },
          {
            type: "matching",
            question: "Match these concepts to their meanings.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Responsibility",
                answer: "Delivering on commitments",
              },
              {
                question: "Tools",
                answer: "Improving efficiency and quality",
              },
              {
                question: "Profession",
                answer: "Contributing to team growth",
              },
            ],
          },
          {
            type: "fitb",
            question: "A professional programmer ensures ______ progress when working in teams.",
            answers: ["confused", "clear", "vague"],
            correct_answer: null,
            correct_answers: {
              blank1: "clear",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "ATDD ensures proper testing based on ______ expectations.",
            answers: ["user's", "coder's", "manager's"],
            correct_answer: null,
            correct_answers: {
              blank1: "user's",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 2,
        messages: [
          {
            author_message:
              "Welcome back! Ready to explore the journey to mastering software professionalism?",
            learner_message: "Absolutely! How do programmers start this journey?",
          },
          {
            author_message:
              "It starts with adopting good habits—writing clean code, testing thoroughly, and constantly learning. It’s a process, like climbing a hill step by step.",
            learner_message:
              "Writing clean code sounds essential. What do you mean by ‘clean code’?",
          },
          {
            author_message:
              "Clean code is easy to read, understand, and maintain. For example, using clear naming conventions for variables and functions—think of it as labeling your spice jars at home!",
            learner_message: "Haha, I like that comparison! What about constant learning?",
          },
          {
            author_message:
              "Tech evolves rapidly. Professionals stay updated by reading books, attending workshops, and following industry trends. It’s like watering a plant to keep it growing!",
            learner_message: "Okay, so what role does testing play in mastery?",
          },
          {
            author_message:
              "Testing ensures your code works as intended. Professionals write tests beforehand to avoid surprises, much like proofreading an essay before submission.",
            learner_message: "Cool, is there one big takeaway from this journey?",
          },
          {
            author_message:
              "It’s about embracing a mindset of responsibility and craftsmanship. Professionals don’t cut corners—they aim for perfection while collaborating effectively.",
            learner_message:
              "Got it. Is there more to professionalism beyond the technical skills?",
          },
          {
            author_message:
              "You bet! Next, let’s delve into the value of professional interactions—handling pressure, communicating well, and thriving in teams. Excited?",
            learner_message: "Can’t wait to learn more about professional interactions!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What does clean code emphasize?",
            answers: ["Complex naming", "Readability and maintenance", "Ignoring readability"],
            correct_answer: "Readability and maintenance",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What’s the role of testing in software professionalism?",
            answers: [
              "Avoiding writing tests",
              "Ensuring code works as intended",
              "Guessing outcomes",
            ],
            correct_answer: "Ensuring code works as intended",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the concepts with their examples.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Clean code",
                answer: "Using clear naming conventions",
              },
              {
                question: "Learning",
                answer: "Reading industry books",
              },
              {
                question: "Testing",
                answer: "Checking code works beforehand",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the practices with their benefits.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Responsibility",
                answer: "Ensures reliability",
              },
              {
                question: "Craftsmanship",
                answer: "Aims for perfection",
              },
              {
                question: "Collaboration",
                answer: "Works effectively in teams",
              },
            ],
          },
          {
            type: "fitb",
            question: "Professionals write _______ before coding to avoid surprises.",
            answers: ["tests", "random scenarios", "stories"],
            correct_answer: null,
            correct_answers: {
              blank1: "tests",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Clean code is similar to labeling ______ jars for clarity.",
            answers: ["spice", "paper", "water"],
            correct_answer: null,
            correct_answers: {
              blank1: "spice",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 3,
        messages: [
          {
            author_message:
              "It’s great to see you back! Let’s dive into handling professional interactions. Ready?",
            learner_message: "Yes! How do professionals handle pressure at work?",
          },
          {
            author_message:
              "Professionals stay calm, communicate clearly, and prioritize tasks. They avoid making impulsive decisions and focus on finding solutions step by step.",
            learner_message:
              "That sounds challenging! How can communication skills help with teamwork?",
          },
          {
            author_message:
              "Good communication prevents misunderstandings and builds trust. Imagine clearly explaining your ideas during a group project—it creates harmony and efficiency.",
            learner_message: "So it's all about being clear and transparent, right?",
          },
          {
            author_message:
              "Exactly! Professionals also actively listen to others. For instance, asking for feedback on your code can improve it while showing respect for team members’ opinions.",
            learner_message: "Listening sounds powerful. How do teams thrive together then?",
          },
          {
            author_message:
              "Teamwork flourishes when everyone takes responsibility, communicates effectively, and shares the workload evenly. Think of it like rowing a boat; everyone needs to paddle in sync!",
            learner_message: "That’s a great visual! What’s the key learning here?",
          },
          {
            author_message:
              "Professionals thrive under pressure by using their technical and interpersonal skills, embracing responsibility, and communicating effectively—all while supporting their team.",
            learner_message:
              "Got it! Thanks for teaching me all this. Any words of encouragement to wrap up?",
          },
          {
            author_message:
              "You’ve learned a lot today! Keep practicing professionalism, and soon you’ll inspire others in your journey. Keep growing—every step counts!",
            learner_message: "Thank you! I’ll definitely apply these lessons and keep improving!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "How do professionals handle pressure?",
            answers: [
              "Communicate clearly and prioritize tasks",
              "Ignore problems and hope for the best",
              "Make impulsive decisions",
            ],
            correct_answer: "Communicate clearly and prioritize tasks",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What builds trust in teamwork?",
            answers: ["Listening actively", "Ignoring feedback", "Talking over team members"],
            correct_answer: "Listening actively",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the practices to their analogies.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Teamwork",
                answer: "Rowing a boat in sync",
              },
              {
                question: "Listening",
                answer: "Asking for feedback",
              },
              {
                question: "Pressure Handling",
                answer: "Prioritizing and solving step by step",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the concepts with their benefits.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Communication",
                answer: "Prevents misunderstandings",
              },
              {
                question: "Responsibility",
                answer: "Builds trust in the team",
              },
              {
                question: "Support",
                answer: "Shares workload evenly",
              },
            ],
          },
          {
            type: "fitb",
            question: "Professional teamwork thrives when members ______ responsibility.",
            answers: ["ignore", "take", "deny"],
            correct_answer: null,
            correct_answers: {
              blank1: "take",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Professionals handle pressure by ______ tasks effectively.",
            answers: ["prioritizing", "skipping", "delaying"],
            correct_answer: null,
            correct_answers: {
              blank1: "prioritizing",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
    ],
  },
  {
    titles: [
      "How to Master Ethical Programming",
      "What if Clean Code Leads to Success",
      "Why Professionals Say No",
      "Your Guide to Ethical Coding",
      "Become the Clean Code Expert",
      "Say Yes to Programming Excellence",
    ],
    conversations: [
      {
        conversation_id: 1,
        messages: [
          {
            author_message:
              "Hey there! Have you ever wondered why some products have certain trademarks or big capital letters in their names?",
            learner_message:
              "Oh, yeah! Is that just to make them look fancy, or is there more to it?",
          },
          {
            author_message:
              "Great question! It's actually a way for manufacturers to protect their brand identity. Those trademarks show ownership or special usage rights over a designation.",
            learner_message: "Interesting! Can anyone just create a trademark, or are there rules?",
          },
          {
            author_message:
              "Yes, there are rules. Companies must register their trademarks legally, and they’re protected against unauthorized use. For example, think of iconic brands like Nike or Coca-Cola—they’re trademarks!",
            learner_message:
              "That makes sense. But wait, why do books go out of their way to use those trademarks correctly?",
          },
          {
            author_message:
              "Ah, books do that to respect legal rights and avoid lawsuits. Misusing trademarks could lead to consequences, like fines or angry letters from lawyers!",
            learner_message:
              "Whoa, good to know! But is there anything special about the warranties mentioned too?",
          },
          {
            author_message:
              "Absolutely! The author and publisher often include disclaimers to protect themselves—essentially saying, 'We tried our best, but use at your own risk!'",
            learner_message:
              "Haha, so they’re just covering their bases, right? What happens if someone misuses information from a book?",
          },
          {
            author_message:
              "Exactly! If misuse happens, the publisher usually isn’t held liable for any accidental damages, like a broken computer or wrong results.",
            learner_message:
              "Got it! One last thing—is there a reason why books offer special bulk sales?",
          },
          {
            author_message:
              "Oh, yes! Bulk sales often cater to organizations for training or branding purposes. Imagine a tech company gifting books to their employees—that’s a clever move, right?",
            learner_message:
              "That’s smart! So, if someone does bulk orders, does it include customized covers or content?",
          },
          {
            author_message:
              "Bingo! Businesses can often negotiate for custom covers or even tailored materials to fit their brand goals.",
            learner_message:
              "Now I’m curious about the ethics behind coding and programming. Is there a code of conduct for professional programmers?",
          },
          {
            author_message:
              "Absolutely! In fact, ethics and professionalism in programming are key topics. Let’s dive into them in the next chat!",
            learner_message: "Oh wow, I can't wait to learn more about programming ethics!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Why do manufacturers use trademarks?",
            answers: ["To look stylish", "To protect brand identity", "To avoid taxes"],
            correct_answer: "To protect brand identity",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What is the purpose of disclaimers in books?",
            answers: [
              "To cover accidental damages",
              "To guarantee perfection",
              "To protect authors legally",
            ],
            correct_answer: "To protect authors legally",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the terms to their definitions:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Trademark",
                answer: "Brand protection",
              },
              {
                question: "Disclaimer",
                answer: "Legal safeguarding",
              },
              {
                question: "Bulk orders",
                answer: "Custom sales options",
              },
            ],
          },
          {
            type: "matching",
            question: "Match examples to their purposes:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Nike",
                answer: "Trademark ownership",
              },
              {
                question: "Custom covers",
                answer: "Branding goals",
              },
              {
                question: "Disclaimers in books",
                answer: "Avoid liability",
              },
            ],
          },
          {
            type: "fitb",
            question: "Books often respect _____ due to legal rights.",
            answers: ["trademarks", "warranties", "names"],
            correct_answer: null,
            correct_answers: {
              blank1: "trademarks",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Bulk orders allow businesses to ______ their branding.",
            answers: ["negotiate", "customize", "increase"],
            correct_answer: null,
            correct_answers: {
              blank1: "customize",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 2,
        messages: [
          {
            author_message:
              "Alright! Let’s start talking about ethics in programming. Ever wondered what makes a programmer truly professional?",
            learner_message:
              "Hmm, professionalism… Does it have to do with working hard and keeping promises?",
          },
          {
            author_message:
              "You’re on to it! A professional programmer is disciplined, ethical, and reliable—they own their work and take responsibility for it.",
            learner_message:
              "Taking responsibility sounds important. But does it mean they never make mistakes?",
          },
          {
            author_message:
              "Good question! Mistakes happen, but professionals do their best to reduce errors and learn from them. Think of it like doctors following the ‘Do no harm’ rule.",
            learner_message:
              "Oh, like coding carefully to avoid breaking systems—makes sense. How about saying ‘No’? Why is it considered professional?",
          },
          {
            author_message:
              "Saying ‘No’ is crucial! Sometimes programmers face impossible deadlines or requests. Politely declining is better than promising something they can’t deliver.",
            learner_message:
              "I see—better safe than sorry, right? Is there a cost to always saying ‘Yes’ then?",
          },
          {
            author_message:
              "Exactly. Constantly saying ‘Yes’ can lead to burnout or bad results. Professionals strike a balance that keeps their quality high.",
            learner_message:
              "That’s clever. But doesn’t teamwork require compromise, like agreeing to help the team succeed?",
          },
          {
            author_message:
              "It does, but professionals prioritize the big picture, ensuring any agreement contributes positively to the project overall.",
            learner_message:
              "Got it. What about meeting deadlines while preserving quality? Is that possible?",
          },
          {
            author_message:
              "Great question—balancing quality and deadlines requires planning, time management, and realistic expectations. Let’s discuss time management next!",
            learner_message: "Wow, time management sounds like a game-changer for all programmers!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What does professionalism in programming emphasize?",
            answers: [
              "Making no mistakes",
              "Taking responsibility and reliability",
              "Working quickly",
            ],
            correct_answer: "Taking responsibility and reliability",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "Why is saying ‘No’ sometimes professional?",
            answers: [
              "Because working alone is better",
              "Because it avoids making unrealistic promises",
              "Because it speeds up work",
            ],
            correct_answer: "Because it avoids making unrealistic promises",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match ethics concepts to their examples:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Do no harm",
                answer: "Minimize coding errors",
              },
              {
                question: "Saying No",
                answer: "Avoiding impossible requests",
              },
              {
                question: "Burnout",
                answer: "Excessive commitments",
              },
            ],
          },
          {
            type: "matching",
            question: "Match behaviors to ethical outcomes:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Take responsibility",
                answer: "Reliable delivery",
              },
              {
                question: "Prioritize teamwork",
                answer: "Better collaboration",
              },
              {
                question: "Manage deadlines",
                answer: "Balanced output",
              },
            ],
          },
          {
            type: "fitb",
            question: "Professional programmers follow the rule, 'Do no ______.'",
            answers: ["harm", "mistakes", "break"],
            correct_answer: null,
            correct_answers: {
              blank1: "harm",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Saying ‘No’ helps avoid ______ deadlines.",
            answers: ["unrealistic", "flexible", "missed"],
            correct_answer: null,
            correct_answers: {
              blank1: "unrealistic",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 3,
        messages: [
          {
            author_message:
              "Hey! Now let’s talk about mastering time management as a programmer. Are you ready?",
            learner_message: "Absolutely! Time management sounds exciting.",
          },
          {
            author_message:
              "Awesome! First rule: learn to prioritize tasks. Avoid rabbit holes by focusing on what matters most.",
            learner_message: "Interesting! Does that mean reducing distractions too?",
          },
          {
            author_message:
              "Exactly! Distractions kill productivity. Some programmers use 'time-boxing’ or techniques like the Pomodoro method to stay focused.",
            learner_message: "Pomodoro? That sounds like a pasta dish!",
          },
          {
            author_message:
              "Haha, it’s a fun time management trick! Work in blocks of time and take short breaks—kind of like sprinting and resting.",
            learner_message: "Nice! But what about handling messy tasks? Any advice there?",
          },
          {
            author_message:
              "For messy tasks, break them into smaller chunks. Every small win builds momentum. Marshes and swamps won’t seem so intimidating anymore!",
            learner_message:
              "Great tip! So, what happens when a programmer gets stuck in a mental blind alley?",
          },
          {
            author_message:
              "Ah, when stuck, take a step back or ask for help. Reflecting or collaborating often clears the path forward.",
            learner_message:
              "Wow, collaboration sounds useful. Okay, summarize the key lessons we've learned today!",
          },
          {
            author_message:
              "Absolutely! Understand professionalism, learn to say 'No,' balance quality and deadlines, and master time management techniques. These skills make a programmer unstoppable!",
            learner_message:
              "Thanks so much! I’ll definitely apply these concepts. You’re the best guide!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Which tool helps programmers stay focused?",
            answers: ["Pomodoro method", "Time routine", "Prioritized lists"],
            correct_answer: "Pomodoro method",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What should programmers do when facing blind alleys?",
            answers: ["Ignore the tasks", "Break them into chunks", "Reflect or ask for help"],
            correct_answer: "Reflect or ask for help",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match strategies to outcomes:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Time-boxing",
                answer: "Increased focus",
              },
              {
                question: "Taking breaks",
                answer: "Avoiding burnout",
              },
              {
                question: "Breaking tasks",
                answer: "Simplifying complexity",
              },
            ],
          },
          {
            type: "matching",
            question: "Match processes to their benefits:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Prioritizing",
                answer: "Avoid low-impact tasks",
              },
              {
                question: "Pomodoro technique",
                answer: "Time efficiency",
              },
              {
                question: "Collaborating",
                answer: "Clear paths forward",
              },
            ],
          },
          {
            type: "fitb",
            question: "Messy tasks can be tackled by breaking them into ______ pieces.",
            answers: ["smaller", "tiny", "divided"],
            correct_answer: null,
            correct_answers: {
              blank1: "smaller",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Taking short breaks can prevent ______ fatigue.",
            answers: ["mental", "over", "physical"],
            correct_answer: null,
            correct_answers: {
              blank1: "mental",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
    ],
  },
  {
    titles: [
      "What if teamwork builds trust",
      "How to manage work stress well",
      "Why professionalism matters so much",
      "Your step-by-step to project success",
      "Mastering collaboration in the workplace",
      "The secret to ethical problem solving",
    ],
    conversations: [
      {
        conversation_id: 1,
        messages: [
          {
            author_message:
              "Hey there! Ever experienced a high-pressure work environment? Let me share a story about handling deadlines effectively.",
            learner_message:
              "Oh, absolutely! How did you manage to keep things on track under such pressure?",
          },
          {
            author_message:
              "Well, coordination was key. My colleague Joe and I operated like Batman and Robin. We'd meet daily, rebuild the schedule, and clear obstacles. For example, we'd grab lunch for someone skipping firewall config due to hunger!",
            learner_message:
              "That sounds so proactive! Did the team always cooperate when you stepped in like that?",
          },
          {
            author_message:
              "Most of the time, yes! We used ethical and creative techniques to encourage ambition and resolve issues quickly. It wasn’t about yelling; it was about finding what solved the problem and helped the team succeed.",
            learner_message:
              "But wasn’t that exhausting? How did you deal with roadblocks that kept popping up?",
          },
          {
            author_message:
              "It was tiring but necessary. We’d engage supervisors if needed, even directors. The goal was simple: get things done with dedication and collaboration.",
            learner_message:
              "I admire your persistence! Was there a moment that made you reflect on such intense collaboration?",
          },
          {
            author_message:
              "A pivotal moment was realizing everyone’s perception differs. Joe saw technical staff differently compared to legal teams. He didn't manage legal teams like us. I began wondering what professionalism looked like in the tech industry.",
            learner_message:
              "That’s interesting! So, what’s the difference between the technical and legal teams in Joe’s eyes?",
          },
          {
            author_message:
              "To him, legal teams demonstrated professionalism and independence. Meanwhile, technical teams needed ‘babysitting.’ Makes you question how professionals are perceived, doesn’t it?",
            learner_message:
              "Absolutely! Speaking of professionalism, how can technical professionals demonstrate it better?",
          },
          {
            author_message:
              "Great question! We’ll dive into strategies that help tech professionals gain and showcase trust and respect in the workplace next time. It’s all about building a reputation and delivering consistent quality.",
            learner_message:
              "Wow, that sounds fascinating! Can’t wait to explore how tech pros can change perceptions!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What was key to handling pressure in the team?",
            answers: [
              "Coordination and ethical techniques",
              "Ignoring issues until solved",
              "Delegating all tasks",
            ],
            correct_answer: "Coordination and ethical techniques",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "How did Joe perceive the legal team?",
            answers: [
              "They needed babysitting",
              "They demonstrated professionalism",
              "They lacked independence",
            ],
            correct_answer: "They demonstrated professionalism",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the actions with their descriptions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Buying lunch",
                answer: "Clearing firewall setup obstacles",
              },
              {
                question: "Supervisor meetings",
                answer: "Escalating team roadblocks",
              },
              {
                question: "Daily schedule rebuild",
                answer: "Refining team priorities",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the statements with persons mentioned.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Thought tech staff needed babysitting",
                answer: "Joe",
              },
              {
                question: "Focused on daily team coordination",
                answer: "The Author",
              },
              {
                question: "Trusted legal team's timelines",
                answer: "Joe",
              },
            ],
          },
          {
            type: "fitb",
            question: "Pressure makes ___ .",
            answers: ["teams", "progress", "diamonds"],
            correct_answer: null,
            correct_answers: {
              blank1: "diamonds",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Demonstrating ___ builds trust in the workplace.",
            answers: ["professionalism", "speed", "skills"],
            correct_answer: null,
            correct_answers: {
              blank1: "professionalism",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 2,
        messages: [
          {
            author_message:
              "Hi again! Let’s explore how technical professionals can gain trust. It starts with proving you're reliable.",
            learner_message: "I’m intrigued! How do you show reliability in the workplace?",
          },
          {
            author_message:
              "Consistency is crucial. Deliver quality work, meet deadlines, and communicate honestly. Trust builds when you follow through on promises.",
            learner_message:
              "That makes sense! But what if unexpected obstacles arise and deadlines slip?",
          },
          {
            author_message:
              "Great point. Transparency is the best approach. Share challenges early and keep stakeholders informed. This shows integrity and teamwork.",
            learner_message:
              "That sounds manageable! Are there habits professionals can adopt to boost their credibility?",
          },
          {
            author_message:
              "Definitely! Clear, respectful communication is one. For example, email updates with progress summaries and next steps reflect professionalism.",
            learner_message: "Interesting! What role does collaboration play in earning trust?",
          },
          {
            author_message:
              "Huge role! Actively contributing during discussions and offering solutions signals reliability. It’s about working towards shared goals.",
            learner_message:
              "That’s inspiring! Is building trust easier for complete teams or individuals?",
          },
          {
            author_message:
              "It’s a mix but teams gain collective trust faster through success. Speaking of team dynamics, what do you think makes collaboration seamless?",
            learner_message:
              "Hmm, maybe transparent goals and mutual respect? Can we explore those ideas next?",
          },
          {
            author_message:
              "Absolutely! Next, we’ll uncover the secrets to seamless collaboration, discussing transparency, shared goals, and respect in depth.",
            learner_message:
              "This sounds so enlightening! I’m really looking forward to learning about better collaboration!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What helps build trust in professionals?",
            answers: [
              "Transparency and consistency",
              "Blaming obstacles on the team",
              "Avoiding communication",
            ],
            correct_answer: "Transparency and consistency",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "How can professionals boost credibility?",
            answers: [
              "Respectful communication",
              "Overpromising and under-delivering",
              "Ignoring deadlines",
            ],
            correct_answer: "Respectful communication",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the actions to their benefits.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Delivering quality work",
                answer: "Gains reliability",
              },
              {
                question: "Offering solutions",
                answer: "Signals collaboration",
              },
              {
                question: "Early stakeholder updates",
                answer: "Shows transparency",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the concepts with examples.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Respectful communication",
                answer: "Progress-oriented emails",
              },
              {
                question: "Integrity",
                answer: "Sharing challenges upfront",
              },
              {
                question: "Reliability",
                answer: "Meeting deadlines",
              },
            ],
          },
          {
            type: "fitb",
            question: "Collaborative success builds ___ faster.",
            answers: ["confidence", "trust", "relationships"],
            correct_answer: null,
            correct_answers: {
              blank1: "trust",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Offer ___ during discussions to show teamwork.",
            answers: ["solutions", "criticism", "silence"],
            correct_answer: null,
            correct_answers: {
              blank1: "solutions",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 3,
        messages: [
          {
            author_message:
              "Welcome back! Today, let’s unveil secrets to seamless collaboration. Transparency is a good start.",
            learner_message: "Why does transparency matter so much in team settings?",
          },
          {
            author_message:
              "Because it builds trust! Openly sharing goals, progress, and obstacles fosters mutual understanding and accountability.",
            learner_message:
              "That’s great! How can teams ensure their goals are transparent to everyone involved?",
          },
          {
            author_message:
              "By maintaining clarity – using shared documents, visual aids, and frequent updates. Everyone should know the objective and steps forward.",
            learner_message: "And how does mutual respect tie into working well together?",
          },
          {
            author_message:
              "It keeps everything smooth! Treating each idea seriously and valuing contributions nurtures a positive, productive environment.",
            learner_message:
              "That’s so helpful! Is there anything else teams should prioritize during collaboration?",
          },
          {
            author_message:
              "Absolutely! Active listening enhances collaboration. It ensures voices are heard and builds solutions through understanding.",
            learner_message:
              "Agreed! I feel like I’ve learned so much! Can we summarize the key thoughts so far?",
          },
          {
            author_message:
              "Of course! Collaboration thrives on transparency, shared goals, respect, clarity, and active listening. Apply these principles, and teamwork will truly become seamless!",
            learner_message:
              "Thanks a lot for this journey! I’m excited to use these ideas in my work and continue learning.",
          },
          {
            author_message:
              "The pleasure is mine! Keep applying these lessons and growing your skills. Professionalism and collaboration go a long way in creating success.",
            learner_message:
              "Absolutely! I’m grateful for all these insights and motivated to apply them effectively!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What helps teams achieve transparency?",
            answers: [
              "Shared documents and clear updates",
              "Ignoring goals",
              "Solo productivity focus",
            ],
            correct_answer: "Shared documents and clear updates",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "Why is active listening important in collaboration?",
            answers: [
              "To hear concerns and ideas",
              "To avoid teamwork",
              "To prioritize self-interest",
            ],
            correct_answer: "To hear concerns and ideas",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match principles to their benefits.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Transparency",
                answer: "Fosters trust",
              },
              {
                question: "Active listening",
                answer: "Enhances understanding",
              },
              {
                question: "Mutual respect",
                answer: "Nurtures positivity",
              },
            ],
          },
          {
            type: "matching",
            question: "Match tools with their uses.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Shared documents",
                answer: "Clarifies goals",
              },
              {
                question: "Visual aids",
                answer: "Explains progress",
              },
              {
                question: "Frequent updates",
                answer: "Tracks accountability",
              },
            ],
          },
          {
            type: "fitb",
            question: "Clarity in goals fosters ___ collaboration.",
            answers: ["effective", "tough", "delayed"],
            correct_answer: null,
            correct_answers: {
              blank1: "effective",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Respect nurtures ___ within teams.",
            answers: ["trust", "difficulty", "stress"],
            correct_answer: null,
            correct_answers: {
              blank1: "trust",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
    ],
  },
  {
    titles: [
      "Why trust your own expertise",
      "Your guide to engineering responsibility",
      "What if managers ignored engineers?",
      "How to act like a software pro",
      "Engineering lessons from disasters",
      "Becoming a true professional expert",
    ],
    conversations: [
      {
        conversation_id: 1,
        messages: [
          {
            author_message:
              "Ever wondered what it takes to evolve from being a mere technician to a software professional? Let me paint a picture for you!",
            learner_message:
              "Oh, sure! What's the difference between a technician and a professional?",
          },
          {
            author_message:
              "A technician often follows instructions, but a professional commits, organizes, and collaborates effectively. They own their tasks and make strong decisions.",
            learner_message:
              "Interesting! So, professionals sound more proactive. Can you give me an example?",
          },
          {
            author_message:
              "Absolutely! Imagine Joe working on a big web project. Instead of waiting for instructions, he collaborates with sysadmins and ensures the job moves forward smoothly without delays.",
            learner_message:
              "That's cool! But what if no one listens to professionals? Like managers dismissing their concerns?",
          },
          {
            author_message:
              "Good question. When professionals clearly communicate their knowledge and data, it's a stronger stance. But dismissive managers can lead to consequences, as we'll see later with the Challenger disaster.",
            learner_message: "Oh, I’ve heard about that disaster! What went wrong?",
          },
          {
            author_message:
              "It’s a story of missed signals. Engineers raised serious concerns about faulty O-rings in cold temperatures, but managers ignored them under financial and political pressure.",
            learner_message:
              "Ignoring warnings sounds reckless! How could engineers have communicated better?",
          },
          {
            author_message:
              "While the engineers followed protocols, a stronger, more assertive approach—calling external parties or media—could’ve amplified their concerns. Always act when stakes are high.",
            learner_message: "Wow! What other critical lessons can we learn from this disaster?",
          },
          {
            author_message:
              "Let’s dig deeper into the O-ring issue and explore how technical knowledge impacts key decisions. This will highlight the importance of professionalism and responsibility.",
            learner_message: "I can’t wait to learn more about those O-rings! Let’s dive into it!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What does it mean to move from technician to professional?",
            answers: [
              "Following instructions",
              "Taking ownership and committing",
              "Avoiding teamwork",
            ],
            correct_answer: "Taking ownership and committing",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "Why did managers ignore engineers' warnings about the Challenger?",
            answers: [
              "Overconfidence in data",
              "Financial and political pressure",
              "Feeling engineers were untrained",
            ],
            correct_answer: "Financial and political pressure",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the person to their action:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Engineer",
                answer: "Raised concerns about risks",
              },
              {
                question: "Manager",
                answer: "Dismissed warnings under pressure",
              },
              {
                question: "Joe",
                answer: "Collaborated to move projects forward",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the term to its description:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Technician",
                answer: "Follows instructions strictly",
              },
              {
                question: "Professional",
                answer: "Commits to tasks and collaborates",
              },
              {
                question: "Sysadmin",
                answer: "Supports operations tasks",
              },
            ],
          },
          {
            type: "fitb",
            question:
              "The ___ disaster underscores the need for raising technical concerns boldly.",
            answers: ["Challenger", "Enterprise", "Apollo"],
            correct_answer: null,
            correct_answers: {
              blank1: "Challenger",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Engineers feared the ___ was too cold for O-rings to remain effective.",
            answers: ["temperature", "pressure", "velocity"],
            correct_answer: null,
            correct_answers: {
              blank1: "temperature",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 2,
        messages: [
          {
            author_message:
              "Let’s talk about those fascinating O-rings and the role they had in the Challenger tragedy.",
            learner_message: "Sure! What are O-rings, and why are they so important?",
          },
          {
            author_message:
              "O-rings are rubber seals designed to prevent leaks between connected segments, like keeping hot gas within the rocket’s SRB. They’re tiny but vital!",
            learner_message: "Wait—rubber seals failed to stop the disaster? How?",
          },
          {
            author_message:
              "The cold launch temperatures caused the O-rings to become stiff and lose flexibility. They couldn’t maintain a tight seal, allowing exhaust gas to escape.",
            learner_message:
              "That’s shocking! So engineers predicted the risk, but managers overruled them?",
          },
          {
            author_message:
              "Exactly. Engineers flagged the danger in memos and meetings. But managers hoped for the best under external pressures. Their decision was catastrophic.",
            learner_message: "How could managers have handled this better?",
          },
          {
            author_message:
              "Managers needed to trust their experts, like the engineers. Also, creating robust communication channels could’ve prevented misjudgment.",
            learner_message: "Got it! Do engineers hold any responsibility here too?",
          },
          {
            author_message:
              "Possibly. While they followed protocols, stepping outside the system—contacting media or external auditors—might’ve saved lives. Responsibility includes bold action when needed.",
            learner_message:
              "Hmm, any other concepts tied to this issue, like professionalism or ethics in engineering?",
          },
          {
            author_message:
              "Absolutely! Next, we'll discuss ethics and how engineers balance tough decisions while maintaining integrity and honoring their craft.",
            learner_message: "This sounds fascinating, let’s explore engineering ethics next!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Why did the O-rings fail during the Challenger launch?",
            answers: ["Excess weight", "Cold temperatures", "Incorrect installation"],
            correct_answer: "Cold temperatures",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What could engineers have done differently to prevent the tragedy?",
            answers: [
              "Stop working on the project",
              "Alert external parties",
              "Create stiffer O-rings",
            ],
            correct_answer: "Alert external parties",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the process to its role in engineering failure:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "O-Rings",
                answer: "Rubber seals failed in cold",
              },
              {
                question: "SRB",
                answer: "Source of exhaust gas",
              },
              {
                question: "Managers",
                answer: "Ignored key warnings",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the term to its description:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Engineers",
                answer: "Advised against risks",
              },
              {
                question: "Communication",
                answer: "Vital for trust in decisions",
              },
              {
                question: "Professionals",
                answer: "Should act boldly under risk",
              },
            ],
          },
          {
            type: "fitb",
            question: "O-rings lost their ability to seal due to ___ conditions.",
            answers: ["cold", "hot", "high pressure"],
            correct_answer: null,
            correct_answers: {
              blank1: "cold",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Trusting ___ was a key flaw in the Challenger incident.",
            answers: ["intuitions", "engineering data", "managers’ hope"],
            correct_answer: null,
            correct_answers: {
              blank1: "managers’ hope",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 3,
        messages: [
          {
            author_message:
              "Let’s wrap things up with the most important topic—engineering ethics and professionalism!",
            learner_message: "Yes, please! How would you define engineering ethics?",
          },
          {
            author_message:
              "It’s about responsibility—using technical knowledge honestly, maintaining clean work, and making decisions prioritizing safety over convenience or pressure.",
            learner_message: "Sounds deep! Is professionalism tied to ethics as well?",
          },
          {
            author_message:
              "Certainly! Professionals act with honor and respect for their craft. They make responsible decisions and communicate effectively, even in difficult scenarios.",
            learner_message: "Got it! Any tips for dealing with unreasonable demands, ethically?",
          },
          {
            author_message:
              "Say 'no' when safety or project integrity is compromised. Communicate risks clearly and suggest alternatives instead of yielding to pressure.",
            learner_message: "Cool! So, balancing ethics, integrity, and work pressure is key?",
          },
          {
            author_message:
              "You bet! And adopting attitudes of honesty, self-respect, and pride in your work helps you grow as an engineer and a professional.",
            learner_message:
              "Nice! Do engineers ever face dilemmas where the risk-reward balance is tough?",
          },
          {
            author_message:
              "Absolutely. But staying informed, trusting your expertise, and being bold when needed helps you make ethical decisions effectively.",
            learner_message:
              "Thanks for summarizing! These lessons on ethics and professionalism are invaluable.",
          },
          {
            author_message:
              "You’ve got it! Remember, success isn’t just about solving problems—it’s also about solving them with integrity. Keep building these skills.",
            learner_message:
              "Thanks again! I’ll commit to applying all these valuable insights in my career!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What is a central part of engineering ethics?",
            answers: [
              "Convenience in decision-making",
              "Maintaining integrity and safety",
              "Ignoring risks under pressure",
            ],
            correct_answer: "Maintaining integrity and safety",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "Why should professionals say 'no' to unreasonable demands?",
            answers: ["Avoid extra work", "Ensure project integrity", "Keep managers happy"],
            correct_answer: "Ensure project integrity",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the term to its value in engineering ethics:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Honesty",
                answer: "Using knowledge truthfully",
              },
              {
                question: "Safety",
                answer: "Prioritizing human lives",
              },
              {
                question: "Integrity",
                answer: "Standing by your expertise",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the characteristic to professionalism:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Self-respect",
                answer: "Value your own work",
              },
              {
                question: "Collaboration",
                answer: "Work well with others",
              },
              {
                question: "Bold decisions",
                answer: "Act under tough risks",
              },
            ],
          },
          {
            type: "fitb",
            question: "Professionals prioritize ___, even under work pressure.",
            answers: ["integrity", "convenience", "speed"],
            correct_answer: null,
            correct_answers: {
              blank1: "integrity",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Success in engineering means solving with ___ values.",
            answers: ["ethical", "time-saving", "commercial"],
            correct_answer: null,
            correct_answers: {
              blank1: "ethical",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
    ],
  },
  {
    titles: [
      "Why Programming Wasn't Easy in 1968",
      "Your Journey to Programming Mastery",
      "How to Build Programs Without Computers",
      "What if You Could Hack Modems",
      "Discover the Drama of Early Programming",
      "Programming Adventures of Tim and Friends",
    ],
    conversations: [
      {
        conversation_id: 1,
        messages: [
          {
            author_message:
              "Hey! Did you know back in 1968, programming wasn’t as simple as using a laptop? We had to learn from books and write programs without even having access to a computer!",
            learner_message:
              "Wait, how did you even test your programs if you didn’t have a computer?",
          },
          {
            author_message:
              "We didn’t! We wrote code for fun and imagined how it would run. It was all about the thrill of creating something new, just like making art but with code!",
            learner_message: "That sounds wild! So, when did you finally get access to a computer?",
          },
          {
            author_message:
              "In high school, they got an ASR-33 Teletype and a dial-up modem connected to a Univac 1108. That’s when the magic started. Tim and I became the unofficial operators of the machine!",
            learner_message: "What’s a Teletype? And how did you connect to the modem?",
          },
          {
            author_message:
              "Good question! A Teletype is basically a printer that could send and receive data to and from the computer. To connect to the modem, you’d dial a phone number, wait for the sound, then press a button to set up the connection!",
            learner_message: "Whoa! Did anyone else get to use the machine, or was it all yours?",
          },
          {
            author_message:
              "Honestly, we pretty much ran the place! Even the teachers left us alone. They trusted us to handle everything—writing programs, printing results, and organizing student work!",
            learner_message:
              "That sounds like a lot of responsibility. Did you mess with the modem lock, too?",
          },
          {
            author_message:
              "Totally! The phone had a lock, but I learned to ‘tap’ the number on the switch hook to bypass it. Being a drummer really came in handy there—timing was everything!",
            learner_message:
              "This is so fascinating! But what happened after school hours? Did you ever just play around with the computer for fun?",
          },
          {
            author_message:
              "Oh, absolutely! We wrote all kinds of quirky programs—drawing graphs in ASCII, generating random words, you name it. Programming wasn’t just work; it was our playground. Next, we’ll dive into how these early experiences led to my first programming job!",
            learner_message: "",
          },
          {
            author_message: "",
            learner_message: "Can’t wait to learn more about your first programming job!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What did the ASR-33 Teletype do?",
            answers: [
              "Printed and sent data to computers",
              "Played musical notes",
              "Made phone calls",
            ],
            correct_answer: "Printed and sent data to computers",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What skill helped bypass the phone lock?",
            answers: ["Drumming skills", "Programming expertise", "Typing speed"],
            correct_answer: "Drumming skills",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the term with its description:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "ASR-33 Teletype",
                answer: "Printer that sent data",
              },
              {
                question: "IITran",
                answer: "Interpreted programming language",
              },
              {
                question: "Switch hook dialing",
                answer: "Bypassed phone lock",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the key event with its outcome:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "High school got Teletype",
                answer: "Created programming opportunities",
              },
              {
                question: "Teachers gave freedom",
                answer: "Students managed computer lab",
              },
              {
                question: "Tapped phone numbers",
                answer: "Unlocked modem access",
              },
            ],
          },
          {
            type: "fitb",
            question: "The ASR-33 Teletype connected to the ____.",
            answers: ["Univac 1108", "IBM 360", "Macintosh"],
            correct_answer: null,
            correct_answers: {
              blank1: "Univac 1108",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Tim and I ran the ____ lab at school.",
            answers: ["computer", "science", "chemistry"],
            correct_answer: null,
            correct_answers: {
              blank1: "computer",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 2,
        messages: [
          {
            author_message:
              "So, after high school, Tim and I landed our first programming job at ASC Tabulating. We were just 18 and decided to skip college!",
            learner_message: "Whoa, straight into the workforce? Why not go to college?",
          },
          {
            author_message:
              "We felt college would slow us down. Programming was evolving fast, and we wanted to dive right in. Plus, we were already experienced, thanks to our high school adventures.",
            learner_message:
              "That makes sense. What was it like working as a professional programmer at 18?",
          },
          {
            author_message:
              "It was exciting but also a mix of good and bad experiences. We worked with experienced programmers who taught us the ropes, but it wasn’t all smooth sailing—there were some tough lessons too.",
            learner_message: "What kind of programs did you work on at ASC Tabulating?",
          },
          {
            author_message:
              "Mostly we worked on business-related software. It was very different from the fun, experimental programs we wrote in high school. But the learning curve was steep, and that made it thrilling.",
            learner_message: "Who were some of your mentors or colleagues during this time?",
          },
          {
            author_message:
              "People like Bill Hohri and Frank Ryder were great influences. They helped us understand what it meant to work professionally in software development.",
            learner_message: "Did these experiences shape your future career decisions?",
          },
          {
            author_message:
              "Absolutely! The ups and downs helped me realize the value of quality and discipline in programming. I had to learn resilience and adaptability.",
            learner_message:
              "That’s really inspiring! What happened after you left ASC Tabulating? Did you jump right into another programming role?",
          },
          {
            author_message:
              "Not quite. There were some detours—lawn mower repair and other odd jobs—but each experience taught me something invaluable. Next, I’ll share how those detours helped me grow as a programmer and a person!",
            learner_message: "",
          },
          {
            author_message: "",
            learner_message:
              "This sounds fascinating! Let’s explore how those detours shaped your programming journey.",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Why did Tim and the author skip college?",
            answers: [
              "They felt college would slow them down",
              "They didn’t have enough money",
              "There were no good programming colleges",
            ],
            correct_answer: "They felt college would slow them down",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What was a key lesson learned at ASC Tabulating?",
            answers: [
              "Value of quality and discipline",
              "How to repair printers",
              "Creating games exclusively",
            ],
            correct_answer: "Value of quality and discipline",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the mentor with their impact:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Bill Hohri",
                answer: "Professional guidance",
              },
              {
                question: "Frank Ryder",
                answer: "Technical expertise",
              },
              {
                question: "Big Jim Carlin",
                answer: "Workplace wisdom",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the event with its impact:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Skipping college",
                answer: "Focus on rapid growth",
              },
              {
                question: "First job at ASC Tabulating",
                answer: "Professional experience",
              },
              {
                question: "Odd jobs",
                answer: "Life lessons",
              },
            ],
          },
          {
            type: "fitb",
            question: "First job as a programmer was at ____.",
            answers: ["ASC Tabulating", "IBM", "Univac"],
            correct_answer: null,
            correct_answers: {
              blank1: "ASC Tabulating",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "A mentor at ASC Tabulating was ____ Hohri.",
            answers: ["Bill", "Tim", "Jim"],
            correct_answer: null,
            correct_answers: {
              blank1: "Bill",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 3,
        messages: [
          {
            author_message:
              "Hey! Those detours were life-changing. I eventually learned valuable lessons while working at Teradyne, where I met incredible colleagues and improved my programming skills.",
            learner_message: "Teradyne sounds like another big step. What made it special?",
          },
          {
            author_message:
              "First, the team: people like Russ Ashdown and Ken Finder taught me professionalism and teamwork. Then there was Mike Carew—he and I formed such a dynamic duo for coding projects!",
            learner_message:
              "That sounds like teamwork at its best. Did you work on interesting projects there?",
          },
          {
            author_message:
              "Definitely! Mike and I developed systems and even created quirky ideas like ‘The Electronic Receptionist.’ It was a mix of serious work and creative exploration.",
            learner_message: "That’s really innovative. Any funny stories from your time there?",
          },
          {
            author_message:
              "Plenty! I spent a year in England with Mike Kergozou, where our programming ideas often led to pub chats and bike rides. It wasn’t all work; we had a lot of fun too!",
            learner_message: "Sounds like a balanced experience. What came after Teradyne?",
          },
          {
            author_message:
              "I started partnering with other fantastic programmers, like Jim Newkirk, to build start-ups. That’s where we really honed our skills and learned persistence in creating software solutions.",
            learner_message: "What’s your biggest takeaway from this part of your journey?",
          },
          {
            author_message:
              "Collaboration is everything. From Teradyne to Object Mentor, working with talented people taught me how teamwork powers innovation and growth.",
            learner_message:
              "That’s a great lesson! Thanks for sharing these insights. It’s been an incredible journey to follow so far!",
          },
          {
            author_message:
              "Thank you! You’ve picked up some key lessons yourself: persistence, creativity, and teamwork are essential elements of success. Keep exploring and improving!",
            learner_message: "",
          },
          {
            author_message: "",
            learner_message:
              "I’m grateful for all I’ve learned. I’ll definitely use this knowledge to grow in my journey!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Who did the author work closely with at Teradyne?",
            answers: ["Mike Carew", "Tim Lloyd", "Richard Fogel"],
            correct_answer: "Mike Carew",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What was ‘The Electronic Receptionist’?",
            answers: ["An innovative software project", "A Teletype machine", "A pub invention"],
            correct_answer: "An innovative software project",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the colleague with their contribution:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Mike Carew",
                answer: "Dynamic duo partner",
              },
              {
                question: "Ken Finder",
                answer: "Professional guidance",
              },
              {
                question: "Jim Newkirk",
                answer: "Startup collaborator",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the project with its outcome:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "The Electronic Receptionist",
                answer: "Creative innovation",
              },
              {
                question: "Start-ups with Jim",
                answer: "Persistence in software",
              },
              {
                question: "England collaboration",
                answer: "Balanced creativity",
              },
            ],
          },
          {
            type: "fitb",
            question: "Teradyne offered valuable lessons in teamwork and ____.",
            answers: ["innovation", "stress", "conflict"],
            correct_answer: null,
            correct_answers: {
              blank1: "innovation",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Object Mentor focused on software and ____ consulting.",
            answers: ["process", "architecture", "hardware"],
            correct_answer: null,
            correct_answers: {
              blank1: "process",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
    ],
  },
  {
    titles: [
      "How to Become a Professional Programmer",
      "Your Path to Clean Coding",
      "What if Stars Could Speak?",
      "Why the Crab Nebula Matters",
      "Secrets of the Programming World",
      "Discovering Sauron's Eye in Space",
    ],
    conversations: [
      {
        conversation_id: 1,
        messages: [
          {
            author_message:
              "Hey! Software development is more than just writing code. It's a profession, and that’s exactly what this book is about—defining professionalism.",
            learner_message:
              "That sounds interesting! But what does 'professionalism' mean in programming?",
          },
          {
            author_message:
              "Great question! Professionalism means having the right attitudes, disciplines, and actions to produce quality work and collaborate effectively. It's the cornerstone of trust in any project.",
            learner_message: "Okay, but how do programmers learn to act professionally?",
          },
          {
            author_message:
              "Well, many learn the hard way—through trial and error, like I did. Experience teaches you about commitment, honesty, and delivering meaningful results.",
            learner_message: "Can you share a personal story about how you learned this?",
          },
          {
            author_message:
              "Sure! My first programming job involved endless hours updating IBM manuals. It was dull, but I learned to appreciate persistence and detail-oriented work.",
            learner_message:
              "Persistence sounds important! Did you make any big mistakes in your first job?",
          },
          {
            author_message:
              "Oh yes, plenty! On one project, I miscalculated IDs when writing my Easycoder program, causing chaos. From that, I learned to test all work thoroughly before releasing it.",
            learner_message:
              "Yikes! Testing seems critical. Any tips for beginners to avoid such blunders?",
          },
          {
            author_message:
              "Absolutely. Always break tasks into smaller chunks, double-check your steps, and seek feedback from peers. Collaboration can save you from costly missteps.",
            learner_message:
              "This is helpful! Speaking of collaboration, what makes a team of programmers truly successful?",
          },
          {
            author_message:
              "Ah, teamwork—the magic ingredient! But it’s a big topic. In our next chat, we’ll dive into the secrets of effective collaboration in programming teams.",
            learner_message: "Awesome; I can't wait to learn more about working in teams!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What does professionalism in programming involve?",
            answers: [
              "Writing complex code",
              "Having the right attitudes and disciplines",
              "Focusing only on creativity",
            ],
            correct_answer: "Having the right attitudes and disciplines",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What was key to avoiding mistakes in programming?",
            answers: [
              "Seeking punishment for errors",
              "Testing work thoroughly",
              "Ignoring feedback",
            ],
            correct_answer: "Testing work thoroughly",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match attitudes to their descriptions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Persistence",
                answer: "Continually working through challenges.",
              },
              {
                question: "Attention to detail",
                answer: "Double-checking for errors.",
              },
              {
                question: "Collaboration",
                answer: "Seeking feedback from peers.",
              },
            ],
          },
          {
            type: "matching",
            question: "Match roles to traits.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Programmer",
                answer: "Writes and tests code.",
              },
              {
                question: "Team Leader",
                answer: "Guides group projects.",
              },
              {
                question: "Supervisor",
                answer: "Oversees tasks and progress.",
              },
            ],
          },
          {
            type: "fitb",
            question: "The best programmers learn through ______ experience.",
            answers: ["trial and error", "luck", "prediction"],
            correct_answer: null,
            correct_answers: {
              blank1: "trial and error",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Break tasks into smaller ______ for better results.",
            answers: ["portions", "pieces", "chunks"],
            correct_answer: null,
            correct_answers: {
              blank1: "chunks",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 2,
        messages: [
          {
            author_message:
              "Let’s dive deeper! A successful programming team is built on communication and mutual respect.",
            learner_message: "Mutual respect? Why is that so crucial?",
          },
          {
            author_message:
              "Because trust fosters collaboration! Teams thrive when members listen, support each other, and resolve conflicts constructively.",
            learner_message: "That makes sense. But what about communication—any tips there?",
          },
          {
            author_message:
              "Definitely. Use clear, concise language and avoid assumptions. Tools like Slack or Jira can organize workflows and keep everyone on the same page.",
            learner_message: "Cool! What if team members disagree on a solution?",
          },
          {
            author_message:
              "Conflict isn’t bad—it’s a chance to innovate! Encourage debates, hear everyone out, and aim for consensus—not domination.",
            learner_message:
              "Great advice! Any examples of effective teamwork you've been part of?",
          },
          {
            author_message:
              "Sure! On one project, we overcame clashing opinions by testing multiple methods until we found the best approach. Experimentation built trust and creativity.",
            learner_message:
              "That's inspiring. But team projects seem overwhelming for beginners. Any advice for starting out?",
          },
          {
            author_message:
              "Start small—join collaborative coding exercises or contribute to open-source projects. These are great ways to build teamwork skills!",
            learner_message:
              "Interesting! Speaking of open-source, how do programmers even begin contributing to such projects?",
          },
          {
            author_message:
              "Fantastic question! In our next session, we’ll explore open-source programming and how to dive into this exciting world.",
            learner_message: "Can't wait to uncover the secrets of open-source programming!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What is important for team success?",
            answers: ["Mutual respect", "Competition", "Individuality"],
            correct_answer: "Mutual respect",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "How can you communicate effectively in teams?",
            answers: ["Use clear language", "Avoid debates", "Stick to assumptions"],
            correct_answer: "Use clear language",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match terms to their definitions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Conflict",
                answer: "Opportunity to innovate.",
              },
              {
                question: "Consensus",
                answer: "Finding a mutually agreed solution.",
              },
              {
                question: "Experimentation",
                answer: "Testing ideas to find the best one.",
              },
            ],
          },
          {
            type: "matching",
            question: "Match tools to their purposes.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Slack",
                answer: "Facilitates team communication.",
              },
              {
                question: "Jira",
                answer: "Tracks project workflows and tasks.",
              },
              {
                question: "GitHub",
                answer: "Hosts open-source code repositories.",
              },
            ],
          },
          {
            type: "fitb",
            question: "Start small by joining collaborative ______ exercises.",
            answers: ["coding", "writing", "gaming"],
            correct_answer: null,
            correct_answers: {
              blank1: "coding",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Conflict is an opportunity for ______ innovation.",
            answers: ["constructive", "harmful", "passive"],
            correct_answer: null,
            correct_answers: {
              blank1: "constructive",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 3,
        messages: [
          {
            author_message:
              "Ready to explore open-source programming? It's where creativity meets global collaboration!",
            learner_message: "Definitely! But what exactly is open-source programming?",
          },
          {
            author_message:
              "Open-source projects are publicly shared codebases that anyone can modify and improve. Think of it as teamwork, but across the globe!",
            learner_message: "Wow, that sounds exciting! How do you join an open-source community?",
          },
          {
            author_message:
              "Start by picking a project that interests you—GitHub is loaded with great options. Read the contribution guidelines carefully!",
            learner_message: "Got it. Are there beginner-friendly projects out there?",
          },
          {
            author_message:
              "Absolutely! Look for ones labeled 'good first issue.' They're designed to help new contributors get started easily.",
            learner_message: "What skills do I need to contribute to open-source projects?",
          },
          {
            author_message:
              "Programming skills help, but so do documentation, testing, and debugging skills. Don't worry—most contributions are team efforts!",
            learner_message: "That's awesome! Any final tips for getting started?",
          },
          {
            author_message:
              "Yes! Build a GitHub profile, connect with developers, and contribute consistently. It’s not just about code—it’s about community and learning!",
            learner_message:
              "Thanks so much for the guidance! Open-source programming really opens up a world of possibilities.",
          },
          {
            author_message:
              "You’ve learned so much today—teamwork, professionalism, and the wonders of open-source! Keep exploring, practicing, and growing.",
            learner_message: "Thank you! I’m excited to apply all this and continue learning!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What is open-source programming?",
            answers: [
              "Private code projects",
              "Publicly shared codebases",
              "Unofficial coding exercises",
            ],
            correct_answer: "Publicly shared codebases",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What label indicates beginner-friendly issues?",
            answers: ["High priority", "Good first issue", "Small bug"],
            correct_answer: "Good first issue",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match skills to their importance.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Programming",
                answer: "Modifying and improving code.",
              },
              {
                question: "Testing",
                answer: "Ensuring code works correctly.",
              },
              {
                question: "Documentation",
                answer: "Writing clear project guidelines.",
              },
            ],
          },
          {
            type: "matching",
            question: "Match terms to their definitions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "GitHub",
                answer: "Platform for open-source projects.",
              },
              {
                question: "Open-source",
                answer: "Publicly shared code anyone can improve.",
              },
              {
                question: "Contributing",
                answer: "Adding to an existing project.",
              },
            ],
          },
          {
            type: "fitb",
            question: "Open-source programming combines creativity with ______ collaboration.",
            answers: ["global", "virtual", "domestic"],
            correct_answer: null,
            correct_answers: {
              blank1: "global",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Start with projects labeled ______ first issue.",
            answers: ["good", "small", "quick"],
            correct_answer: null,
            correct_answers: {
              blank1: "good",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
    ],
  },
  {
    titles: [
      "How to Overcome Coding Challenges",
      "Your Guide to Coding Confidence",
      "What if Code Could Speak?",
      "Why Failure Leads to Growth",
      "Secrets to Mastering Error-Sense",
      "Unleashing Your Inner Programmer",
    ],
    conversations: [
      {
        conversation_id: 1,
        messages: [
          {
            author_message:
              "Welcome to the fascinating world of coding! Do you know what makes coding so rewarding?",
            learner_message: "Hmm, not really. Is it the problem-solving aspect?",
          },
          {
            author_message:
              "Exactly! It’s like solving a puzzle but with logic and creativity combined. Plus, it’s all about turning ideas into reality, just like magic.",
            learner_message:
              "That sounds amazing! But it also sounds hard. How did you first learn to code?",
          },
          {
            author_message:
              "Oh, it wasn’t easy! I had to start with basics like COBOL and Fortran, and I even typed blind on an IBM keypunch machine to practice. Confidence was key, let me tell you.",
            learner_message:
              "Wait, typing blind? That sounds scary. How did you build up the confidence?",
          },
          {
            author_message:
              "Well, I realized my fingers knew where the keys were all along. It’s all about trusting yourself and sensing when you make a mistake. This instant feedback loop helps you learn faster.",
            learner_message:
              "Wow, instant learning through error sounds cool. Is it the same with coding?",
          },
          {
            author_message:
              "Absolutely! Coding is all about error sense. Spotting bugs quickly lets you fix them with ease and improves your skills over time. It’s like sharpening a tool.",
            learner_message:
              "Interesting! But juggling code that works, fits into systems, and satisfies users sounds overwhelming. How do you manage it?",
          },
          {
            author_message:
              "Ah, that’s where preparedness comes in! Before you code, make sure you eliminate distractions, focus fully, and ensure clarity in your goals. When you’re prepared, magic happens.",
            learner_message:
              "Got it! So distractions are the enemy of coding. But are there any funny or epic fails you’ve had in coding?",
          },
          {
            author_message:
              "Oh, let me tease you with a snippet—ever heard of writing code at 3 am thinking it’s genius, only to redo it completely the next day? That’s where things get hilarious and painful!",
            learner_message:
              "Ha! I can’t wait to dive into more coding tales and lessons in our next chat!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What is coding compared to in the conversation?",
            answers: ["A puzzle", "A sport", "A storybook"],
            correct_answer: "A puzzle",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What’s a key aspect to learning coding effectively?",
            answers: ["Ignoring errors", "Building confidence", "Working alone"],
            correct_answer: "Building confidence",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the coding principles to their descriptions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Error sense",
                answer: "Spot bugs quickly and learn",
              },
              {
                question: "Preparedness",
                answer: "Eliminate distractions before coding",
              },
              {
                question: "Focus",
                answer: "Clarity in goals",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the coding metaphors to their concepts.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Puzzle",
                answer: "Combines logic with creativity",
              },
              {
                question: "Magic",
                answer: "Turns ideas into reality",
              },
              {
                question: "Sharpening a tool",
                answer: "Improves skills over time",
              },
            ],
          },
          {
            type: "fitb",
            question: "Coding is all about staying _____.",
            answers: ["prepared", "focused", "curious"],
            correct_answer: null,
            correct_answers: {
              blank1: "prepared",
              blank2: "focused",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Spotting errors quickly helps achieve _____.",
            answers: ["confidence", "success", "clarity"],
            correct_answer: null,
            correct_answers: {
              blank1: "confidence",
              blank2: "success",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 2,
        messages: [
          {
            author_message:
              "So, remember me mentioning 3 am coding disasters? Let’s talk about what makes coding at odd hours so tricky.",
            learner_message: "Yes, 3 am! Why does it lead to bad code?",
          },
          {
            author_message:
              "At that hour, your brain is exhausted. Coding needs concentration and focus, and late nights just don’t support that. Distractions multiply, mistakes sneak in!",
            learner_message: "I see. But why do people even code late at night?",
          },
          {
            author_message:
              "Ambition! Many coders dream of success, even rich startups. They work long hours imagining rewards, but stress and tiredness can backfire. Been there myself!",
            learner_message:
              "Ah, working hard to chase dreams—sounds motivating. What’s your advice for managing such situations?",
          },
          {
            author_message:
              "Plan smart! Know your limits, create balance, and focus on quality over hours invested. Coding isn’t about crunch—it’s about growth and sustainability.",
            learner_message:
              "That’s valuable advice. So, does preparation kick distractions aside completely?",
          },
          {
            author_message:
              "Not entirely—you need techniques like mindfulness, breaks, and organizing tasks to truly vanquish distractions. It’s all part of coding like a pro!",
            learner_message:
              "Mindfulness and breaks—sounds refreshing. Could creativity help eliminate distractions too?",
          },
          {
            author_message:
              "Absolutely! Coding’s creative aspect keeps you engaged emotionally. When you’re immersed in problem-solving, distractions fade naturally into the background.",
            learner_message:
              "Exciting! What’s a creative approach some coders use that others might miss?",
          },
          {
            author_message:
              "Coming up next, I'll introduce principles to fuse creativity with discipline—like writing readable code that expresses intent precisely. Stay tuned!",
            learner_message:
              "Readable code that expresses intent? I can’t wait to learn more about it next!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Why is coding at 3 am often counterproductive?",
            answers: ["High stress", "Low focus and distraction", "Dreaming about startups"],
            correct_answer: "Low focus and distraction",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What trait keeps distractions manageable during coding?",
            answers: ["Mindfulness", "Speed", "Ambition"],
            correct_answer: "Mindfulness",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match coding challenges to solutions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Exhaustion",
                answer: "Mindfulness and breaks",
              },
              {
                question: "Distraction",
                answer: "Sustainable focus",
              },
              {
                question: "Stress",
                answer: "Organized task breakdown",
              },
            ],
          },
          {
            type: "matching",
            question: "Match concepts to coding aspirations.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Creativity",
                answer: "Engages emotional immersion",
              },
              {
                question: "Readable code",
                answer: "Expresses intent precisely",
              },
              {
                question: "Planning",
                answer: "Balances growth and quality",
              },
            ],
          },
          {
            type: "fitb",
            question: "Mindfulness and creativity are key to _____.",
            answers: ["coding effectively", "completing tasks", "minimizing stress"],
            correct_answer: null,
            correct_answers: {
              blank1: "coding effectively",
              blank2: "minimizing stress",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Sustainability and focus create better _____.",
            answers: ["code", "careers", "teams"],
            correct_answer: null,
            correct_answers: {
              blank1: "code",
              blank2: "careers",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 3,
        messages: [
          {
            author_message:
              "Let’s continue where we left off—writing readable code. Ready to dig in?",
            learner_message: "Totally! What makes code readable though?",
          },
          {
            author_message:
              "Readable code reveals your intent. It’s not about comments but crafting it so others can understand and maintain it easily.",
            learner_message: "Oh, like writing a story for other programmers! So intent is key?",
          },
          {
            author_message:
              "Exactly! Well-written code saves time, prevents misunderstandings, and reduces bugs. Think of it as teamwork through text.",
            learner_message:
              "Teamwork through text—love the idea. Any tips on how to achieve this?",
          },
          {
            author_message:
              "Start with a clear structure, follow best practices, and keep everything consistent. Simplicity ensures your code is friendly to others.",
            learner_message:
              "Got it, simplicity and clarity rule. But how do readability and creativity connect?",
          },
          {
            author_message:
              "Creativity is your secret weapon for balance. Complexity can be clever but dangerous—creative coders simplify without losing power.",
            learner_message:
              "Balance sounds like art! To wrap up, what’s one personal habit that improved your coding?",
          },
          {
            author_message:
              "Confidence and error-sense. Closing the feedback loop lets you grow exponentially. Coding maturity kicks off when you nurture error awareness.",
            learner_message:
              "Confidence in feedback, clarity in code, and creativity in balance—such great lessons today. Thanks for sharing!",
          },
          {
            author_message:
              "You’re welcome! Keep exploring and coding. It’s a continuous journey, and every step matters—you’ve got this!",
            learner_message:
              "Thanks so much! I’ll definitely apply these lessons and keep learning to improve my coding skills!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What does readable code achieve?",
            answers: ["Prevents bugs", "Saves time", "Both of the above"],
            correct_answer: "Both of the above",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What fuels coding maturity?",
            answers: ["Error feedback", "Complexity", "Patterns"],
            correct_answer: "Error feedback",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match coding ideas to descriptions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Readable code",
                answer: "Friendly to teammates",
              },
              {
                question: "Error sense",
                answer: "Rapid error detection",
              },
              {
                question: "Creativity",
                answer: "Simplifies complexity",
              },
            ],
          },
          {
            type: "matching",
            question: "Match behavior to positive coding outcomes.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Feedback loop",
                answer: "Exponential learning",
              },
              {
                question: "Simplicity",
                answer: "Clarity in code",
              },
              {
                question: "Teamwork",
                answer: "Shared understanding",
              },
            ],
          },
          {
            type: "fitb",
            question: "Coding maturity grows with confidence and _____.",
            answers: ["error-sense", "teamwork", "creativity"],
            correct_answer: null,
            correct_answers: {
              blank1: "error-sense",
              blank2: "confidence",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Readable code expresses _____ clearly.",
            answers: ["intent", "simplicity", "patterns"],
            correct_answer: null,
            correct_answers: {
              blank1: "intent",
              blank2: "simplicity",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
    ],
  },
  {
    titles: [
      "Why Coding When Tired Is Risky",
      "Your Guide to Productive Coding",
      "What if You Focus Better?",
      "How to Manage Coding Distractions",
      "Secrets to Better Programming Days",
      "Avoid These Coding Mistakes!",
    ],
    conversations: [
      {
        conversation_id: 1,
        messages: [
          {
            author_message:
              "Coding at 3 am? Bad idea! Trust me, I've done it, and it caused endless headaches.",
            learner_message: "Why is late-night coding such a bad idea? Sounds thrilling!",
          },
          {
            author_message:
              "Thrilling, sure, but here's the kicker: it's risky. Poor designs like sending a program 'mail' to itself can spiral into disaster.",
            learner_message:
              "Wait, sending mail to itself? That sounds kinda funny. What went wrong?",
          },
          {
            author_message:
              "Oh, it was hilariously bad! It created infinite mail loops and strange errors. Fixing those bugs became a team joke for years!",
            learner_message: "Yikes! Is it all because coding while tired messes with our focus?",
          },
          {
            author_message:
              "Exactly! When you're tired, dedication shifts from quality to just grinding hours, which leads to messy decisions.",
            learner_message: "So, how can developers avoid this trap?",
          },
          {
            author_message:
              "Simple: prioritize sleep and health. Eight focused hours of coding beats chaotic 18-hour marathons any day!",
            learner_message:
              "Got it! But what about other distractions, like worries? Do those affect coding too?",
          },
          {
            author_message:
              "Absolutely. Worry acts like a background process, stealing your focus and productivity. You can't code your best with a stressed brain.",
            learner_message:
              "Wow, that’s tough. So, how can we manage our worries better while coding?",
          },
          {
            author_message:
              "Good question! We’ll dive deep next time into how to quiet those nagging worries and partition your mental space effectively.",
            learner_message: "I’m curious now! Can’t wait to learn how to deal with that next!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Why is it risky to code while tired?",
            answers: [
              "It helps you write faster.",
              "It leads to poor design and errors.",
              "It makes coding more fun.",
            ],
            correct_answer: "It leads to poor design and errors.",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What is better than working long hours?",
            answers: [
              "Coding for 3 hours at night.",
              "Staying focused for 8 hours a day.",
              "Coding continuously without breaks.",
            ],
            correct_answer: "Staying focused for 8 hours a day.",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the actions to their consequences.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Coding while tired",
                answer: "Messy designs",
              },
              {
                question: "Prioritizing sleep",
                answer: "Better productivity",
              },
              {
                question: "Ignoring health",
                answer: "Decreased focus",
              },
            ],
          },
          {
            type: "matching",
            question: "Match terms to their descriptions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Infinite mail loop",
                answer: "Cause of endless errors",
              },
              {
                question: "Focused coding",
                answer: "Quality over quantity",
              },
              {
                question: "Late-night work",
                answer: "Risky",
              },
            ],
          },
          {
            type: "fitb",
            question: "Coding at late hours can lead to ____. ",
            answers: ["Focused results", "Messy code", "Better teamwork"],
            correct_answer: null,
            correct_answers: {
              blank1: "Messy code",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Prioritizing ____ can improve coding productivity.",
            answers: ["Sleep", "Stress", "Worry"],
            correct_answer: null,
            correct_answers: {
              blank1: "Sleep",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 2,
        messages: [
          {
            author_message:
              "Worries? They're like coding bugs for your mind! Steal attention and drain focus.",
            learner_message:
              "So true! How do developers quiet background distractions like worries?",
          },
          {
            author_message:
              "I partition my time! Spend an hour addressing the worry, like checking on people or resolving arguments.",
            learner_message: "But what if you can’t resolve the worry in an hour? What then?",
          },
          {
            author_message:
              "That’s fair. It’s about reducing the worry's priority. Shrinking its 'mental footprint' helps you refocus on coding.",
            learner_message: "That makes sense! Is it okay to manage worries during office hours?",
          },
          {
            author_message:
              "Sometimes, yes, as long as it improves productivity. But professionals resolve anxieties before work starts.",
            learner_message:
              "Got it. So what about worries that just refuse to go away? Are developers doomed?",
          },
          {
            author_message:
              "Not at all! Humans are versatile. It takes practice, but managing distractions gets easier over time.",
            learner_message:
              "I’m starting to see how this links to productivity. Do worries impact flow too?",
          },
          {
            author_message:
              "Yes, flow—aka 'the Zone'—can help. But ironically, it makes you miss the bigger picture. More on that next!",
            learner_message: "Wow, the Zone sounds tricky. I’m excited to dive into that next!",
          },
          {
            author_message:
              "Next time, we’ll explore whether ‘flow’ is really helping you code, or if it’s secretly a productivity trap.",
            learner_message: "This sounds fascinating, let’s explore it next!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What is the best way to manage worries affecting coding?",
            answers: [
              "Ignore the worries.",
              "Spend dedicated time on the worry.",
              "Work harder to override them.",
            ],
            correct_answer: "Spend dedicated time on the worry.",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What is a common mistake made by developers?",
            answers: [
              "Resolving worries quickly.",
              "Forcing themselves to work through worry.",
              "Getting plenty of rest before coding.",
            ],
            correct_answer: "Forcing themselves to work through worry.",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match scenarios to solutions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Sick child worry",
                answer: "Call and check in",
              },
              {
                question: "Argument with spouse",
                answer: "Talk it through",
              },
              {
                question: "Money problem",
                answer: "Plan finances",
              },
            ],
          },
          {
            type: "matching",
            question: "Match disruptions with their effects.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Major worries",
                answer: "Stolen concentration",
              },
              {
                question: "Partitioned time",
                answer: "Reduced anxiety",
              },
              {
                question: "Unresolved stress",
                answer: "Blocked productivity",
              },
            ],
          },
          {
            type: "fitb",
            question: "Worries act as a ____ process stealing focus.",
            answers: ["Background", "Creative", "Known"],
            correct_answer: null,
            correct_answers: {
              blank1: "Background",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Managing mental distractions helps ____ work quality.",
            answers: ["Lower", "Enhance", "Match"],
            correct_answer: null,
            correct_answers: {
              blank1: "Enhance",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 3,
        messages: [
          {
            author_message:
              "Here’s the scoop: ‘flow’ or ‘the Zone’ feels productive, but there’s a hidden cost.",
            learner_message: "You mean the euphoria of coding might not be all good?",
          },
          {
            author_message:
              "Exactly! In the Zone, you lose sight of the big picture, causing mistakes you'll revisit later.",
            learner_message: "Wow, but isn’t the Zone supposed to make us hyper-efficient?",
          },
          {
            author_message:
              "It feels that way, but it’s a deceptive loop. More speed, less design clarity. And that's a problem.",
            learner_message: "That’s tricky! How do we avoid falling into the Zone, then?",
          },
          {
            author_message:
              "Step away! Emails, tweets, lunch breaks—they're lifesavers. Also, try pair programming; it’s communication-heavy.",
            learner_message:
              "Got it! Pair programming sounds neat. Any other ways to boost productivity safely?",
          },
          {
            author_message:
              "Test-driven development—TDD—is great! It guides you with failing tests, keeping context grounded post-disruptions.",
            learner_message:
              "Pairing and TDD? Those are gold! So, how can developers manage creative roadblocks, like writer’s block?",
          },
          {
            author_message:
              "Creative input is key! Read all sorts of material—science fiction, mysteries, anything stimulating. Plus, sleep well!",
            learner_message:
              "Summing it up: Worries, fatigue, and even the Zone can impact coding quality. Staying mindful and balanced is crucial!",
          },
          {
            author_message:
              "You nailed it! Remember, coding isn’t just about speed—it’s about thoughtful, sustainable practices. Keep growing!",
            learner_message: "Thanks a ton! I’ll apply all this and continue learning more!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Why should you avoid the Zone while coding?",
            answers: [
              "It helps complete projects quickly.",
              "It reduces clarity in design decisions.",
              "It is a state of intense focus.",
            ],
            correct_answer: "It reduces clarity in design decisions.",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What is a benefit of pair programming?",
            answers: [
              "Entering the Zone faster.",
              "Encouraging communication and clarity.",
              "Increasing focus through solitude.",
            ],
            correct_answer: "Encouraging communication and clarity.",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match productivity boosters with their benefits.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "TDD",
                answer: "Context preservation",
              },
              {
                question: "Pair programming",
                answer: "Collaboration clarity",
              },
              {
                question: "Reading diverse material",
                answer: "Enhanced creative input",
              },
            ],
          },
          {
            type: "matching",
            question: "Match disruptions with solutions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Entering the Zone",
                answer: "Take breaks",
              },
              {
                question: "Creative block",
                answer: "Stimulating reading",
              },
              {
                question: "Design mistakes",
                answer: "More clarity during programming",
              },
            ],
          },
          {
            type: "fitb",
            question: "The Zone compromises ____ clarity in coding.",
            answers: ["Design", "Speed", "Basics"],
            correct_answer: null,
            correct_answers: {
              blank1: "Design",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Creative input such as ____ primes productivity.",
            answers: ["Science fiction", "Music", "Silence"],
            correct_answer: null,
            correct_answers: {
              blank1: "Science fiction",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
    ],
  },
  {
    titles: [
      "How to Debug Like a Pro",
      "Why Coding is a Creative Craft",
      "Your Guide to Debugging Skills",
      "What if Debugging Was Easy?",
      "Mastering Software Development Pace",
      "Secrets to Solving Coding Problems",
    ],
    conversations: [
      {
        conversation_id: 1,
        messages: [
          {
            author_message:
              "Hey there! Coding is not just technical—it’s surprisingly creative! Did you know certain activities spark creativity better than others?",
            learner_message: "Oh, like what? What helps you create better coding ideas?",
          },
          {
            author_message:
              "Space opera works wonders for me! Watching TV or listening to music? Not so much—unless it’s for presentations or talks.",
            learner_message:
              "Space opera? That sounds interesting! Why would something like that help?",
          },
          {
            author_message:
              "It’s all about inspiration and imagination! Space opera challenges me with epic, complex worlds—perfect for stimulating problem-solving in coding.",
            learner_message:
              "Got it! So creativity matters even for coding. Are there other tips for staying productive while coding?",
          },
          {
            author_message:
              "Absolutely! Debugging efficiently saves tons of time. It’s all part of coding, not just an annoying detour!",
            learner_message: "Debugging, huh? That sounds tricky. How do you approach it?",
          },
          {
            author_message:
              "Once, we solved a massive bug using memory inspections and counters—it felt like detective work to unravel the mystery!",
            learner_message:
              "Wow, that’s intense. Did you ever figure out why debugging feels like solving puzzles?",
          },
          {
            author_message:
              "It’s because every bug has an underlying story—variables out of sync, missed details. And fixing them teaches persistence and precision!",
            learner_message:
              "Interesting! But speaking of bugs, how can you avoid creating them in the first place?",
          },
          {
            author_message:
              "Great question! That’s what we’ll dive into next. Tools like Test Driven Development (TDD) reduce bugs and debugging time drastically. Let’s explore that process!",
            learner_message:
              "Can’t wait to learn more about Test Driven Development! Sounds super useful!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Which activity helps the author spark creativity for coding?",
            answers: ["Listening to music", "Watching TV", "Space opera"],
            correct_answer: "Space opera",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "Why does debugging feel like solving puzzles?",
            answers: [
              "It requires creativity",
              "It has an underlying story",
              "It’s about imagination",
            ],
            correct_answer: "It has an underlying story",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the activity to its benefit:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Listening to music",
                answer: "Helps create presentations",
              },
              {
                question: "Watching TV",
                answer: "Doesn’t usually help creativity",
              },
              {
                question: "Space opera",
                answer: "Stimulates coding creativity",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the bug-solving method to its purpose.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Counters",
                answer: "Track variable changes",
              },
              {
                question: "Inspecting memory",
                answer: "Spot problem areas",
              },
              {
                question: "Logs",
                answer: "Print critical moments",
              },
            ],
          },
          {
            type: "fitb",
            question: "Debugging saves time because coding and debugging are ___.",
            answers: ["separate", "connected", "opposite"],
            correct_answer: null,
            correct_answers: {
              blank1: "connected",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Space opera helps spark coding creativity by inspiring _____.",
            answers: ["imagination", "accuracy", "speed"],
            correct_answer: null,
            correct_answers: {
              blank1: "imagination",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 2,
        messages: [
          {
            author_message:
              "Hey! Did you know you can drastically reduce bugs by adopting a specific process? It’s called Test Driven Development!",
            learner_message: "That sounds promising! What is TDD exactly?",
          },
          {
            author_message:
              "It’s a method where you write tests before actual coding. These tests guide your code and catch errors before they happen!",
            learner_message: "Write tests before coding? How does that work in practice?",
          },
          {
            author_message:
              "You start by thinking of the outcome, writing a test for it, and then coding step-by-step until the test passes. It keeps you focused and careful!",
            learner_message:
              "Wow, sounds like a careful way to prevent errors. Is it hard to learn?",
          },
          {
            author_message:
              "It’s a discipline, but not too hard! And trust me, the time it saves on debugging is worth every step.",
            learner_message: "What do you mean by saving time? Like, fewer hours debugging?",
          },
          {
            author_message:
              "Exactly! By catching mistakes early, you avoid long debugging sessions, leading to efficiency and cleaner code.",
            learner_message: "Does TDD help with teamwork too, or is it just for solo developers?",
          },
          {
            author_message:
              "Great question again! TDD creates transparent, reliable code—making teamwork smoother since everyone knows what the code intends to do.",
            learner_message:
              "Awesome! But how do developers deal with unexpected bugs during teamwork efforts?",
          },
          {
            author_message:
              "Glad you asked! Let’s talk about pacing yourself, teamwork strategies, and managing energy in coding. It’s crucial knowledge for every developer!",
            learner_message:
              "Sounds fascinating! Can’t wait to learn about pacing and teamwork strategies next!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What is the key idea behind Test Driven Development (TDD)?",
            answers: [
              "Coding without planning",
              "Writing tests after debugging",
              "Writing tests before coding",
            ],
            correct_answer: "Writing tests before coding",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "How does TDD benefit teamwork?",
            answers: ["Creates cleaner code", "Adds more complexity", "Delays outcomes"],
            correct_answer: "Creates cleaner code",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the TDD step to the benefit:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Write tests first",
                answer: "Prevent major bugs",
              },
              {
                question: "Follow test outcomes",
                answer: "Guide your coding",
              },
              {
                question: "Catch mistakes early",
                answer: "Save time on debugging",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the benefit to the teamwork task:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Transparent code",
                answer: "Improves communication",
              },
              {
                question: "Reliable outcomes",
                answer: "Boosts trust",
              },
              {
                question: "Efficient debugging",
                answer: "Saves team time",
              },
            ],
          },
          {
            type: "fitb",
            question: "Test Driven Development starts by writing _____.",
            answers: ["tests", "code", "logs"],
            correct_answer: null,
            correct_answers: {
              blank1: "tests",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "TDD helps developers improve _____.",
            answers: ["teamwork", "speed", "stress"],
            correct_answer: null,
            correct_answers: {
              blank1: "teamwork",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 3,
        messages: [
          {
            author_message:
              "Hello again! Let’s talk about energy management and pacing yourself for long-term coding success!",
            learner_message:
              "Yes, please! How can developers keep up their energy during projects?",
          },
          {
            author_message:
              "Taking breaks is key! Believe it or not, stepping away from the keyboard—whether driving or showering—can spark solutions.",
            learner_message: "Driving solves coding problems? How does that even happen?",
          },
          {
            author_message:
              "It’s because driving forces you to disengage mentally from your work. This clears your head and gives your creative subconscious a chance to jump in!",
            learner_message:
              "That makes sense! What about showering—is that just refreshing or creative too?",
          },
          {
            author_message:
              "Definitely creative! Showering often triggers fresh thoughts. The water, the relaxation—it all rejuvenates your mind. I’ve solved plenty of coding puzzles there!",
            learner_message:
              "So disengagement is productive too! But what if we’re late on a deadline—how should we manage that?",
          },
          {
            author_message:
              "Good question! Transparency is critical. Always share best, nominal, and worst-case timelines with stakeholders—avoid hope-based planning!",
            learner_message:
              "Interesting strategy. But how do we avoid stress when things get rushed?",
          },
          {
            author_message:
              "By pacing projects realistically, collaborating smartly, and recognizing when to walk away and recharge! It’s about working more sustainably.",
            learner_message:
              "Thanks for sharing! To sum it up, coding success relies on creativity, teamwork, pacing, and smart planning, right?",
          },
          {
            author_message:
              "Exactly! You’ve nailed it! Keep learning and applying these practices—it’s these small tweaks that make coding a joy, not a stress.",
            learner_message:
              "I’m so grateful for these insights! I’ll definitely apply them to my coding journey. Thanks!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What helps developers spark creativity when stuck?",
            answers: ["Working nonstop", "Disengagement", "Performing tests"],
            correct_answer: "Disengagement",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "How should lateness in projects be managed?",
            answers: ["Hope-based planning", "Regular transparency", "Ignore deadlines"],
            correct_answer: "Regular transparency",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the activity to its creative benefit:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Driving home",
                answer: "Disengages mentally",
              },
              {
                question: "Showering",
                answer: "Triggers fresh thoughts",
              },
              {
                question: "Taking breaks",
                answer: "Reduces mental fatigue",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the pacing method to its purpose:",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Sharing timelines",
                answer: "Manage lateness",
              },
              {
                question: "Walking away",
                answer: "Recharge and refresh",
              },
              {
                question: "Realistic pacing",
                answer: "Avoid undue stress",
              },
            ],
          },
          {
            type: "fitb",
            question: "Key to managing lateness is sharing _____.",
            answers: ["optimism", "timelines", "tasks"],
            correct_answer: null,
            correct_answers: {
              blank1: "timelines",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Disengagement boosts coding creativity by clearing _____.",
            answers: ["focus", "energy", "your head"],
            correct_answer: null,
            correct_answers: {
              blank1: "your head",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
    ],
  },
  {
    titles: [
      "Why Coding Ethics Matter",
      "How to Handle Tough Deadlines",
      "Your Guide to Helping and Mentoring",
      "What if Overtime Fails?",
      "Mastering Professional Coding Habits",
      "Handle Stress Like a Pro Developer",
    ],
    conversations: [
      {
        conversation_id: 1,
        messages: [
          {
            author_message:
              "So, you've got a tough deadline? Let me tell you, rushing is never the solution in coding.",
            learner_message: "Really? Why not just work faster to meet the deadline?",
          },
          {
            author_message:
              "If you rush, you'll take shortcuts, make mistakes, and create more problems than you solve. It's a disaster waiting to happen!",
            learner_message: "But what if my boss says I must do it? Isn't it my job?",
          },
          {
            author_message:
              "Your job is to deliver quality work, not false hope. Tell them the truth and suggest reducing the scope instead.",
            learner_message: "Okay, but what about working overtime? Could that help?",
          },
          {
            author_message:
              "Overtime might work in rare cases for short bursts. But it’s risky when stretched longer than two weeks—you burn out and lose efficiency.",
            learner_message: "How do I know if I should agree to overtime, then?",
          },
          {
            author_message:
              "Only agree if you can afford it personally, it’s temporary, and your boss has a backup plan if it fails. Otherwise, walk away.",
            learner_message:
              "Hmm, so rushing and false promises are bad. But what if someone lies and says the code is done when it's not?",
          },
          {
            author_message:
              "Ah, false delivery is trouble! Everyone starts stretching the definition of 'done,' and it snowballs into chaos. A proper definition of 'done' is critical.",
            learner_message: "Got it! What’s a good way to define 'done'?",
          },
          {
            author_message:
              "We’ll talk about defining 'done' with automated acceptance tests in the next conversation. It’s a game changer!",
            learner_message: "Sounds promising! I can’t wait to learn about acceptance tests!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Why is rushing in coding problematic?",
            answers: ["It creates false hope", "It speeds up work", "It reduces mistakes"],
            correct_answer: "It creates false hope",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "When is overtime a viable option?",
            answers: [
              "When it's long-term",
              "If your boss has no backup plan",
              "When it’s temporary and manageable",
            ],
            correct_answer: "When it’s temporary and manageable",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the terms to their definitions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Rushing",
                answer: "Making mistakes under pressure",
              },
              {
                question: "False delivery",
                answer: "Pretending the work is done",
              },
              {
                question: "Overtime",
                answer: "Short bursts to meet deadlines",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the effects to their causes.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Burnout",
                answer: "Extended overtime",
              },
              {
                question: "False hope",
                answer: "Agreeing to rush deadlines",
              },
              {
                question: "Chaos",
                answer: "False definition of 'done'",
              },
            ],
          },
          {
            type: "fitb",
            question: "Rushing in coding leads to ___ and delays tough decisions.",
            answers: ["mistakes", "efficiency", "success"],
            correct_answer: null,
            correct_answers: {
              blank1: "mistakes",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Overtime should have a ___.",
            answers: ["backup plan", "deadline", "budget"],
            correct_answer: null,
            correct_answers: {
              blank1: "backup plan",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 2,
        messages: [
          {
            author_message: "Let's talk about defining 'done'—true delivery starts here!",
            learner_message: "Why do we even need a definition of 'done'?",
          },
          {
            author_message:
              "Without it, teams stretch the meaning, leading to unchecked, non-functional code. It's a recipe for disaster!",
            learner_message: "What’s a reliable way to define it then?",
          },
          {
            author_message:
              "Automated acceptance tests! They verify your work against business needs and must pass before saying you're 'done.'",
            learner_message: "Oh cool! How do these tests work?",
          },
          {
            author_message:
              "You write tests for features in languages like Selenium or Cucumber, and they check if the feature meets stakeholder expectations.",
            learner_message:
              "Does this slow down development? Writing tests sounds time-consuming.",
          },
          {
            author_message:
              "Not at all! Testing frequently saves time by catching problems early instead of fixing them later. It's efficiency in action!",
            learner_message: "Should everyone on the team use these tests or only certain people?",
          },
          {
            author_message:
              "Business analysts and testers should write them, while developers run them often. It's a collaborative effort!",
            learner_message:
              "Making sure everyone works together sounds tricky. Why is collaboration important in coding?",
          },
          {
            author_message:
              "Great question! Let’s dive into the power of collaboration in our next conversation. It’s key for real coding success!",
            learner_message: "I’m excited to explore collaboration now. Let’s go!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "What is the purpose of automated acceptance tests?",
            answers: [
              "To verify the code meets business needs",
              "To delay coding processes",
              "To remove stakeholder involvement",
            ],
            correct_answer: "To verify the code meets business needs",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "Who should write acceptance tests?",
            answers: ["Business analysts and testers", "Only developers", "Only stakeholders"],
            correct_answer: "Business analysts and testers",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match the tools to their uses.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Selenium",
                answer: "Automated acceptance testing",
              },
              {
                question: "Cucumber",
                answer: "Defining 'done' for stakeholders",
              },
              {
                question: "FitNesse",
                answer: "Testing in understandable language",
              },
            ],
          },
          {
            type: "matching",
            question: "Match the effects to their causes.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Efficiency",
                answer: "Testing frequently",
              },
              {
                question: "Stakeholder trust",
                answer: "Automated tests verify feature requirements",
              },
              {
                question: "Chaos",
                answer: "No definition of 'done'",
              },
            ],
          },
          {
            type: "fitb",
            question:
              "Automated acceptance tests should be ___ by stakeholders and business people.",
            answers: ["understandable", "ignored", "complicated"],
            correct_answer: null,
            correct_answers: {
              blank1: "understandable",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "A reliable definition of 'done' prevents ___ delivery.",
            answers: ["false", "rushed", "ignored"],
            correct_answer: null,
            correct_answers: {
              blank1: "false",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
      {
        conversation_id: 3,
        messages: [
          {
            author_message: "Collaboration is essential for successful programming!",
            learner_message: "Why is working with others so critical in coding?",
          },
          {
            author_message:
              "It helps solve tough problems. A fresh perspective sparks ideas you wouldn’t have on your own!",
            learner_message: "But programmers tend to work solo. Isn’t solitude important too?",
          },
          {
            author_message:
              "Sure, balance is key. Dedicate focused time, but also set aside hours to be available for teamwork.",
            learner_message:
              "How do I make sure I’m helping others and not just working on my own code?",
          },
          {
            author_message:
              "Be proactive! If you notice someone struggling, step in to help. Coding together can make a huge difference!",
            learner_message: "What if I’m the one who needs help? Should I feel bad asking for it?",
          },
          {
            author_message:
              "Not at all! Asking for help is a sign of professionalism. It’s better than staying stuck and delaying progress.",
            learner_message: "What is the best way to ask for help without disturbing others?",
          },
          {
            author_message:
              "Plan ahead! Open channels like team rooms or set times for collaboration. Build mentoring relationships as you grow!",
            learner_message:
              "I’ve learned a lot about teamwork, expressing professionalism, and coding integrity!",
          },
          {
            author_message:
              "Fantastic summary! Keep applying what you’ve learned, and always stay curious. You’re on the path to mastery!",
            learner_message:
              "Thank you so much! I’ll strive to grow and share my skills with others!",
          },
        ],
        questions: [
          {
            type: "mc",
            question: "Why is collaboration critical in coding?",
            answers: [
              "It allows programmers to work solo",
              "It inspires fresh ideas and problem-solving",
              "It slows team productivity",
            ],
            correct_answer: "It inspires fresh ideas and problem-solving",
            correct_answers: null,
            items: [],
          },
          {
            type: "mc",
            question: "What is the best way to balance solo work and team collaboration?",
            answers: [
              "Focus only on teamwork",
              "Allow time for both collaboration and focused coding",
              "Avoid interaction with teammates",
            ],
            correct_answer: "Allow time for both collaboration and focused coding",
            correct_answers: null,
            items: [],
          },
          {
            type: "matching",
            question: "Match actions to their benefits.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Helping others",
                answer: "Creates a collaborative coding environment",
              },
              {
                question: "Asking for help",
                answer: "Promotes progress and professionalism",
              },
              {
                question: "Mentoring",
                answer: "Develops younger programmers effectively",
              },
            ],
          },
          {
            type: "matching",
            question: "Match challenges to solutions.",
            answers: [],
            correct_answer: null,
            correct_answers: null,
            items: [
              {
                question: "Feeling stuck",
                answer: "Ask someone for help",
              },
              {
                question: "Team imbalance",
                answer: "Offer proactive support",
              },
              {
                question: "Solitude-only programming",
                answer: "Balance solo work with collaboration",
              },
            ],
          },
          {
            type: "fitb",
            question: "Offering help to others strengthens ___ bonds.",
            answers: ["team", "professional", "unsure"],
            correct_answer: null,
            correct_answers: {
              blank1: "team",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
          {
            type: "fitb",
            question: "Coding success often requires ___ with teammates.",
            answers: ["collaboration", "independence", "confidence"],
            correct_answer: null,
            correct_answers: {
              blank1: "collaboration",
              blank2: "",
              blank3: "",
              blank4: "",
            },
            items: [],
          },
        ],
      },
    ],
  },
];

export const useCourseFromEbook = () => {
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutationFn = async (body: CoursemakerUploadRequest) => {
    let responseData: CoursemakerUploadResponse;
    logger.info("Uploading ebook for course generation ", body);

    if (isMock("generateCourse_ebook")) {
      await sleepAsync(5000);
      responseData = mock as CoursemakerUploadResponse; //await get_random_course_response(body.lang);
    } else {
      const response = await api.post(api.endpoints.uploadEbook, formdataFromObj(body), {
        onUploadProgress: (progress: AxiosProgressEvent) => {
          setUploadProgress(
            progress.total ? (progress.loaded / progress.total) * 100 : progress.bytes,
          );
        },
      });
      responseData = response.data as CoursemakerUploadResponse;
    }

    let result = courseGenerationToCourse(responseData);
    forEachLesson(result, setDefaultSelectedTitle);

    return result;
  };

  const mutation = useMutation({
    mutationKey: ["upload-ebook"],
    mutationFn,
  });

  return { ...mutation, uploadProgress };
};
